import {
  parseExtensionViewSurface,
  type ExtensionViewCommand,
  type ExtensionViewSurface,
} from "@sylviq/ui-react-web/blocks/extension";
import { SuiRenderer } from "@sylviq/ui-react-web/renderer";
import { Badge } from "@sylviq/ui-react-web/components/ui/badge";
import { Button } from "@sylviq/ui-react-web/components/ui/button";
import { Tooltip } from "@sylviq/ui-react-web/components/ui/tooltip";
import { Tabs } from "@sylviq/ui-react-web/components/ui/tabs";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import { CircleHelp } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  ApiRequestError,
  collectEarlierPlantMonths,
  getCultivationClose,
  getExtensionDependencyImports,
  invokeExtensionV2,
  listChartAccounts,
  type ChartAccount,
  previousCloseMonthEnd,
  priorPlantCountsFromClose,
  priorSettingsFromClose,
  type CloseReview,
  type PriorCloseSettings,
  type ExtensionV2Response,
} from "@/lib/api";
import { useOrganization } from "@/lib/organization";
import { useSession } from "@/lib/session";
import { scopeExtensionViewSurface } from "@/features/extensions/scoped-links";
import { closeProfitAndLoss } from "@/features/extensions/ExtensionFrame";
import {
  approvalSummaryFromResult,
  diffReviewValues,
  formatCents,
  formLabelsFromSurface,
  parseCultivationPreview,
  parseSavedAllocations,
  previewFingerprint,
  type CultivationApprovalSummary,
  type CultivationPreview,
} from "@/features/cultivation/cultivation-review";

type HostContext = {
  close: { license_id: string; period: { start: string; end: string } };
  snapshot?: unknown;
  profit_and_loss?: unknown;
  prior_close?: {
    month: string;
    input_identity: string;
    current_input_identity: string;
    ending_counts: {
      mother: number;
      immature: number;
      vegetative: number;
      flowering: number;
    };
  };
  actor_ref?: string;
  year_to_date_months?: Array<{
    year: number;
    month: number;
    begin_end: number[];
    adjustments: {
      mother: number;
      immature: number;
      vegetative: number;
      flowering: number;
    };
  }>;
};

type ViewState =
  | { status: "loading" }
  | { status: "error"; message: string }
  | { status: "loaded"; surface: ExtensionViewSurface };

type AllocationRow = {
  pool: string;
  account: string;
  name: string;
  amount: string;
  percent: string;
  labor: string;
};

const BLANK_ALLOCATION: AllocationRow = {
  pool: "",
  account: "",
  name: "",
  amount: "",
  percent: "",
  labor: "",
};

function fieldValue(
  field: HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement,
): string {
  if (
    field instanceof HTMLInputElement &&
    (field.type === "checkbox" || field.type === "radio")
  ) {
    return field.checked ? "true" : "";
  }
  return field.value.trim();
}

function applyValues(
  root: ParentNode,
  values: Record<string, string>,
  onlyBlank: boolean,
  skip?: ReadonlySet<string>,
): void {
  for (const field of root.querySelectorAll<
    HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
  >("input[name], textarea[name], select[name]")) {
    if (field instanceof HTMLInputElement && field.type === "password")
      continue;
    if (!/^[A-Za-z][A-Za-z0-9._-]{0,80}$/.test(field.name)) continue;
    if (skip?.has(field.name)) continue;
    if (field === document.activeElement) continue;
    const value = values[field.name];
    if (value === undefined) continue;
    if (onlyBlank && fieldValue(field)) continue;
    if (
      field instanceof HTMLInputElement &&
      (field.type === "checkbox" || field.type === "radio")
    ) {
      const checked = value === "true" || value === "on";
      if (field.checked === checked) continue;
      field.checked = checked;
    } else {
      if (field.value === value) continue;
      field.value = value;
    }
    field.dispatchEvent(new Event("input", { bubbles: true }));
    field.dispatchEvent(new Event("change", { bubbles: true }));
  }
}

type AccountInput = { name: string; label: string };

function isAccountInput(name: string): boolean {
  return /^account_[A-Za-z0-9._-]{1,72}$/.test(name);
}

function accountInputs(
  node: ExtensionViewSurface["body"],
  found: AccountInput[] = [],
): AccountInput[] {
  if (node.type === "form") {
    for (const field of node.fields) {
      if (
        isAccountInput(field.name) &&
        !found.some((item) => item.name === field.name)
      ) {
        found.push({ name: field.name, label: field.label });
      }
    }
    return found;
  }
  if (node.type === "card" || node.type === "stack" || node.type === "grid") {
    for (const child of node.children) accountInputs(child, found);
  }
  return found;
}

function withoutAccountInputs(
  node: ExtensionViewSurface["body"],
): ExtensionViewSurface["body"] | null {
  if (node.type === "form") {
    const fields = node.fields.filter((field) => !isAccountInput(field.name));
    return fields.length > 0 ? { ...node, fields } : null;
  }
  if (node.type === "card" || node.type === "stack" || node.type === "grid") {
    const children = node.children
      .map((child) => withoutAccountInputs(child))
      .filter((child): child is ExtensionViewSurface["body"] => child !== null);
    return { ...node, children };
  }
  return node;
}

function allocationValues(
  allocations: readonly AllocationRow[],
): Record<string, string> {
  const values: Record<string, string> = {};
  for (const [index, row] of allocations.entries()) {
    if (
      !row.pool &&
      !row.account &&
      !row.name &&
      !row.amount &&
      !row.percent &&
      !row.labor
    )
      continue;
    const pool =
      row.pool.trim() ||
      (row.account.trim()
        ? `${row.account.trim()}-${index + 1}`
        : `allocation-${index + 1}`);
    values[`allocation_${index}_pool`] = pool;
    for (const field of [
      "account",
      "name",
      "amount",
      "percent",
      "labor",
    ] as const) {
      if (row[field].trim())
        values[`allocation_${index}_${field}`] = row[field].trim();
    }
  }
  return values;
}

const GROUP_HELP: Readonly<Record<string, string>> = {
  "Review setup":
    "Review interval and excise rate. Evidence provenance is taken from the imported METRC reports.",
  "Monthly plant counts":
    "Prefilled from imported METRC plant-inventory reports. Adjustments default to zero; correct them only with supporting evidence.",
  "Rates and circulation":
    "Cost inputs carried from the prior close or entered for this quarter.",
  "Destruction quantities":
    "Prefilled from the imported METRC destroyed-plant report. Review before submitting.",
  "Opening inventory":
    "Inherited from the prior close when available; a correction needs a reason and approval reference.",
  "Journal review":
    "Journal result and rounding applied to the generated postings.",
  "Account mapping":
    "Choose posting accounts from the active chart of accounts.",
  "WIP journal":
    "Inputs used to build the WIP journal. Calculated amounts are reviewed before posting.",
  "COGS journal": "Inputs used to build the cost-of-goods-sold journal.",
  "Excise review":
    "Optional state worksheet, bank-match, payment, and filing review.",
  "Approvals and corrections":
    "Optional approval references and correction details.",
};

function reviewFieldCount(node: ExtensionViewSurface["body"]): number {
  if (node.type === "form") return node.fields.length;
  if (node.type === "card" || node.type === "stack" || node.type === "grid") {
    return node.children.reduce(
      (total, child) => total + reviewFieldCount(child),
      0,
    );
  }
  return 0;
}

function ReviewInputGroups({
  surface,
  onCommand,
}: {
  surface: ExtensionViewSurface;
  onCommand: (invocation: ExtensionViewCommand) => void;
}) {
  const body = surface.body;
  if (body.type !== "card") {
    return <SuiRenderer node={body} onCommand={onCommand} />;
  }
  let groupIndex = 0;
  return (
    <TooltipPrimitive.Provider>
      <div className="grid gap-3">
        {body.children.map((node, index) => {
          if (node.type === "card" && node.title) {
            const fieldCount = reviewFieldCount(node);
            if (fieldCount === 0) return null;
            const open =
              groupIndex++ === 0 || node.title === "Rates and circulation";
            return (
              <details
                key={`${node.title}-${index}`}
                open={open}
                className="rounded-xl border border-[var(--border)]"
              >
                <summary className="flex cursor-pointer items-center gap-2 px-4 py-3 text-sm font-semibold">
                  {node.title}
                  <span className="text-xs font-normal text-[var(--muted-foreground)]">
                    {fieldCount} field{fieldCount === 1 ? "" : "s"}
                  </span>
                  {GROUP_HELP[node.title] ? (
                    <Tooltip label={GROUP_HELP[node.title]!}>
                      <button
                        type="button"
                        aria-label={`About ${node.title}`}
                        className="inline-flex size-5 items-center justify-center rounded-full text-[var(--muted-foreground)] hover:text-[var(--foreground)]"
                        onClick={(event) => {
                          event.preventDefault();
                          event.stopPropagation();
                        }}
                      >
                        <CircleHelp className="size-4" aria-hidden="true" />
                      </button>
                    </Tooltip>
                  ) : null}
                </summary>
                <div className="border-t border-[var(--border)] p-4">
                  <SuiRenderer
                    node={{ type: "stack", gap: "md", children: node.children }}
                    onCommand={onCommand}
                  />
                </div>
              </details>
            );
          }
          return <SuiRenderer key={index} node={node} onCommand={onCommand} />;
        })}
        {(surface.actions ?? []).map((action) => (
          <SuiRenderer
            key={action.id}
            node={{
              type: "button",
              label: action.label,
              command: action.command,
              ...(action.icon === undefined ? {} : { icon: action.icon }),
              ...(action.variant === undefined
                ? {}
                : { variant: action.variant }),
            }}
            onCommand={onCommand}
          />
        ))}
      </div>
    </TooltipPrimitive.Provider>
  );
}

export function ExtensionV2CostingReview({
  installationId,
  viewId,
  context,
  dependencyInstallationId,
  disabled,
  onReviewed,
  initialValues,
  draftOverrides,
  savedValues,
  evidenceKey = "",
  review,
  onDraftChange,
  onOpenImports,
  submitLabel,
  hostBlockers = [],
}: {
  installationId: string;
  viewId: string;
  context: { license_id: string; period: { start: string; end: string } };
  dependencyInstallationId: string | null;
  disabled: boolean;
  onReviewed: (
    inputs: Record<string, unknown> | null,
    formValues?: Record<string, string>,
    approval?: CultivationApprovalSummary,
  ) => void | Promise<void>;
  initialValues?: Record<string, string> | null;
  draftOverrides?: Record<string, string> | null;
  savedValues?: Record<string, string> | null;
  evidenceKey?: string;
  review?: CloseReview | null;
  onDraftChange?: (values: Record<string, string>) => void;
  onOpenImports?: () => void;
  submitLabel?: string;
  hostBlockers?: string[];
}) {
  const { scope } = useOrganization();
  const { state: session } = useSession();
  const actorRef =
    session.status === "authenticated" ? session.user.id : undefined;
  const panelRef = useRef<HTMLDivElement>(null);
  const touchedRef = useRef<Set<string>>(new Set());
  const applyingRef = useRef(false);
  const allocationsTouchedRef = useRef(false);
  const initialValuesRef = useRef(initialValues);
  initialValuesRef.current = initialValues;
  const [state, setState] = useState<ViewState>({ status: "loading" });
  const [accounts, setAccounts] = useState<ChartAccount[]>([]);
  const [accountValues, setAccountValues] = useState<Record<string, string>>(
    {},
  );
  const [hostContext, setHostContext] = useState<HostContext | null>(null);
  const [notice, setNotice] = useState<{
    tone: "saved" | "error";
    text: string;
  } | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [families, setFamilies] = useState({
    wip: true,
    directexall: true,
    cogs: true,
  });
  const [allocations, setAllocations] = useState<AllocationRow[]>([
    { ...BLANK_ALLOCATION },
  ]);
  const [carried, setCarried] = useState<PriorCloseSettings | null>(null);
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [preview, setPreview] = useState<CultivationPreview | null>(null);
  const [previewKey, setPreviewKey] = useState("");
  const [scheduleTab, setScheduleTab] = useState("");
  const identity = [
    context.license_id,
    context.period.start,
    context.period.end,
    review?.review_id ?? "",
    review?.review_revision ?? "",
  ].join("|");
  const identityRef = useRef(identity);
  const identityChangedRef = useRef(false);
  if (identityRef.current !== identity) {
    identityRef.current = identity;
    identityChangedRef.current = true;
    touchedRef.current = new Set();
    allocationsTouchedRef.current = false;
  }

  useEffect(() => {
    setAccountValues({});
  }, [scope?.legal_entity_id, scope?.organization_id]);

  useEffect(() => {
    if (!scope?.organization_id) return;
    let cancelled = false;
    void listChartAccounts()
      .then((result) => {
        if (!cancelled) setAccounts(result);
      })
      .catch(() => {
        if (!cancelled) setAccounts([]);
      });
    return () => {
      cancelled = true;
    };
  }, [scope?.legal_entity_id, scope?.organization_id]);

  useEffect(() => {
    if (!scope?.organization_id) {
      setState({
        status: "error",
        message: "The organization context is unavailable.",
      });
      return;
    }
    let cancelled = false;
    setState({ status: "loading" });
    setNotice(null);
    setCarried(null);
    setPreview(null);
    setPreviewKey("");
    setAllocations([{ ...BLANK_ALLOCATION }]);
    void (async () => {
      const next: HostContext = {
        close: { ...context, period: { ...context.period } },
        ...(actorRef ? { actor_ref: actorRef } : {}),
      };
      const previous = previousCloseMonthEnd(context.period.start);
      if (previous) {
        try {
          const packet = await getCultivationClose({
            license_id: context.license_id,
            period_end: previous.period_end,
          });
          const ending = priorPlantCountsFromClose(packet, previous.month);
          if (ending) {
            next.prior_close = {
              ...ending,
              current_input_identity: ending.input_identity,
            };
          }
          if (!cancelled)
            setCarried(priorSettingsFromClose(packet, previous.month));
          const yearMonths = await collectEarlierPlantMonths(
            context.license_id,
            context.period.start,
            async (periodEnd) => {
              try {
                return await getCultivationClose({
                  license_id: context.license_id,
                  period_end: periodEnd,
                });
              } catch {
                return null;
              }
            },
          );
          if (yearMonths.length > 0) next.year_to_date_months = yearMonths;
        } catch (cause) {
          if (
            !(cause instanceof ApiRequestError) ||
            (cause.status !== 404 && cause.status !== 409)
          ) {
            if (!cancelled)
              setState({
                status: "error",
                message: "The prior close could not be read.",
              });
            return;
          }
        }
      }
      const [imported, profitAndLoss] = await Promise.all([
        dependencyInstallationId
          ? getExtensionDependencyImports(
              scope.organization_id,
              dependencyInstallationId,
            ).catch(() => null)
          : Promise.resolve(null),
        closeProfitAndLoss(context.period).catch(() => null),
      ]);
      if (cancelled) return;
      if (imported?.snapshot !== undefined) next.snapshot = imported.snapshot;
      if (profitAndLoss) next.profit_and_loss = profitAndLoss;
      if (cancelled) return;
      setHostContext(next);
      try {
        const response = await invokeExtensionV2(
          scope.organization_id,
          installationId,
          {
            kind: "view",
            app: "niche-accounting",
            payload: { viewId, hostContext: next },
          },
        );
        if (cancelled) return;
        if (!response.ok) {
          setState({
            status: "error",
            message:
              response.error?.message ??
              "The close-review view is unavailable.",
          });
          return;
        }
        const surface = parseExtensionViewSurface(response.result);
        if (!surface) {
          setState({
            status: "error",
            message: "The extension returned an invalid close-review view.",
          });
          return;
        }
        setState({ status: "loaded", surface });
      } catch {
        if (!cancelled) {
          setState({
            status: "error",
            message: "The close-review view could not be loaded.",
          });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    actorRef,
    attempt,
    context.license_id,
    context.period.end,
    context.period.start,
    dependencyInstallationId,
    installationId,
    scope?.organization_id,
    viewId,
  ]);

  function applyQuietly(
    root: ParentNode,
    values: Record<string, string>,
    onlyBlank: boolean,
    skip?: ReadonlySet<string>,
  ): void {
    applyingRef.current = true;
    try {
      applyValues(root, values, onlyBlank, skip);
    } finally {
      applyingRef.current = false;
    }
  }

  useEffect(() => {
    if (!carried) return;
    setAllocations((current) => {
      if (allocationsTouchedRef.current) return current;
      const blank = current.every(
        (row) =>
          !row.pool &&
          !row.account &&
          !row.name &&
          !row.amount &&
          !row.percent &&
          !row.labor,
      );
      return blank && carried.allocations.length > 0
        ? carried.allocations
        : current;
    });
    const root = panelRef.current;
    if (!root) return;
    applyQuietly(root, carried.values, true);
  }, [carried, state.status]);

  const computedDefaults = useMemo(() => {
    if (state.status !== "loaded") return {};
    const raw = state.surface.metadata?.defaults;
    if (typeof raw !== "string") return {};
    try {
      const parsed: unknown = JSON.parse(raw);
      if (!parsed || typeof parsed !== "object" || Array.isArray(parsed))
        return {};
      return Object.fromEntries(
        Object.entries(parsed as Record<string, unknown>).filter(
          (entry): entry is [string, string] => typeof entry[1] === "string",
        ),
      );
    } catch {
      return {};
    }
  }, [state]);

  useEffect(() => {
    if (state.status !== "loaded") return;
    const root = panelRef.current;
    if (!root) return;
    const draftNow = identityChangedRef.current
      ? {}
      : (initialValuesRef.current ?? {});
    const base = savedValues ?? null;
    const values: Record<string, string> = {};
    for (const field of root.querySelectorAll<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >("input[name], textarea[name], select[name]")) {
      if (field instanceof HTMLInputElement && field.type === "password")
        continue;
      if (!/^[A-Za-z][A-Za-z0-9._-]{0,80}$/.test(field.name)) continue;
      values[field.name] = base
        ? (base[field.name] ?? "")
        : (draftNow[field.name] ?? "");
      if (draftOverrides?.[field.name] !== undefined) {
        values[field.name] = draftOverrides[field.name]!;
      }
    }
    for (const [name, value] of Object.entries(computedDefaults)) {
      if (!values[name]) values[name] = value;
    }
    // Saved review values are the base. A field the user actually touched in
    // this form overlays them; captured defaults and inherited values do not.
    applyQuietly(root, values, false, touchedRef.current);
    identityChangedRef.current = false;

    if (allocationsTouchedRef.current) return;
    if (base) {
      const savedAllocations = parseSavedAllocations(base);
      setAllocations(
        savedAllocations.length > 0
          ? savedAllocations
          : [{ ...BLANK_ALLOCATION }],
      );
      return;
    }
    const draftAllocations = parseSavedAllocations(draftNow);
    if (draftAllocations.length > 0) setAllocations(draftAllocations);
  }, [
    computedDefaults,
    draftOverrides,
    identity,
    initialValues,
    savedValues,
    state.status,
  ]);

  const readValues = useMemo(
    () => () => {
      const root = panelRef.current;
      const values: Record<string, string> = {};
      if (root) {
        for (const field of root.querySelectorAll<
          HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
        >("input[name], textarea[name], select[name]")) {
          if (field.disabled) continue;
          if (field instanceof HTMLInputElement && field.type === "password")
            continue;
          if (!/^[A-Za-z][A-Za-z0-9._-]{0,80}$/.test(field.name)) continue;
          const value = fieldValue(field);
          if (value) values[field.name] = value;
        }
      }
      const selectedFamilies = Object.entries(families)
        .filter(([, selected]) => selected)
        .map(([family]) => family);
      if (selectedFamilies.length > 0)
        values.families = selectedFamilies.join(",");
      Object.assign(values, allocationValues(allocations));
      return values;
    },
    [allocations, families],
  );

  useEffect(() => {
    if (state.status !== "loaded") return;
    const next = readValues();
    setDraft(next);
    onDraftChange?.(next);
  }, [allocations, families, onDraftChange, readValues, state.status]);

  useEffect(() => {
    if (state.status !== "loaded") return;
    const root = panelRef.current;
    if (!root) return;
    const read = (event: Event) => {
      const target = event.target;
      if (
        !applyingRef.current &&
        (target instanceof HTMLInputElement ||
          target instanceof HTMLTextAreaElement ||
          target instanceof HTMLSelectElement)
      ) {
        if (target.name) touchedRef.current.add(target.name);
      }
      const values = readValues();
      setDraft(values);
      onDraftChange?.(values);
    };
    root.addEventListener("input", read);
    root.addEventListener("change", read);
    return () => {
      root.removeEventListener("input", read);
      root.removeEventListener("change", read);
    };
  }, [onDraftChange, readValues, state.status]);

  const accountFields = useMemo(
    () => (state.status === "loaded" ? accountInputs(state.surface.body) : []),
    [state],
  );
  const viewSurface = useMemo(() => {
    if (state.status !== "loaded") return null;
    const body = withoutAccountInputs(state.surface.body) ?? {
      type: "text" as const,
      style: "muted" as const,
      text: "Complete the account mapping above.",
    };
    return { ...state.surface, body };
  }, [state]);
  const labels = useMemo(() => {
    if (state.status !== "loaded") return {};
    return {
      ...formLabelsFromSurface(state.surface),
      ...Object.fromEntries(
        accountFields.map((field) => [field.name, field.label]),
      ),
    };
  }, [
    accountFields,
    state.status,
    state.status === "loaded" ? state.surface : null,
  ]);

  const changed = useMemo(
    () =>
      disabled
        ? []
        : diffReviewValues(savedValues, draft, labels, carried?.values ?? null),
    [carried?.values, disabled, draft, labels, savedValues],
  );

  const livePreviewKey = useMemo(
    () =>
      previewFingerprint({
        values: draft,
        evidenceKey,
        periodStart: context.period.start,
        periodEnd: context.period.end,
      }),
    [context.period.end, context.period.start, draft, evidenceKey],
  );
  const previewStale = Boolean(preview && previewKey !== livePreviewKey);
  const activeSchedule =
    preview?.sections.find((section) => section.id === scheduleTab) ??
    preview?.sections[0] ??
    null;

  async function refreshedHostContext(): Promise<HostContext> {
    if (!hostContext)
      throw new Error("The close-review context is unavailable.");
    let submitted = hostContext;
    if (submitted.prior_close) {
      const previous = previousCloseMonthEnd(context.period.start);
      if (!previous) throw new Error("The prior close month is unavailable.");
      try {
        const packet = await getCultivationClose({
          license_id: context.license_id,
          period_end: previous.period_end,
        });
        const ending = priorPlantCountsFromClose(packet, previous.month);
        if (!ending) throw new Error("The prior close is no longer available.");
        submitted = {
          ...submitted,
          prior_close: {
            ...submitted.prior_close,
            current_input_identity: ending.input_identity,
          },
        };
      } catch (cause) {
        if (
          cause instanceof ApiRequestError &&
          (cause.status === 404 || cause.status === 409)
        ) {
          throw new Error("The prior close is no longer available.");
        }
        throw cause;
      }
    }
    return submitted;
  }

  async function invokeClose(
    command: "costing.review.finalize" | "costing.review.preview",
    values: Record<string, string>,
  ): Promise<Record<string, unknown>> {
    if (!scope?.organization_id)
      throw new Error("The organization context is unavailable.");
    const submitted = await refreshedHostContext();
    const response: ExtensionV2Response = await invokeExtensionV2(
      scope.organization_id,
      installationId,
      {
        kind: "ui_command",
        app: "niche-accounting",
        payload: {
          command,
          values,
          viewId,
          selectedIds: [],
          renderToken: crypto.randomUUID(),
          hostContext: submitted,
        },
      },
    );
    if (!response.ok)
      throw new Error(response.error?.message ?? "close review rejected");
    if (
      !response.result ||
      typeof response.result !== "object" ||
      Array.isArray(response.result)
    ) {
      throw new Error("close review returned an invalid result");
    }
    return response.result as Record<string, unknown>;
  }

  function submitFinal() {
    if (!evidenceReady) {
      setNotice({
        tone: "error",
        text: "Import the required METRC reports before previewing or saving this close.",
      });
      return;
    }
    if (
      disabled ||
      submitting ||
      !hostContext ||
      !scope?.organization_id ||
      state.status !== "loaded"
    )
      return;
    const values = readValues();
    if (!values.families) {
      setNotice({ tone: "error", text: "Choose at least one journal family." });
      return;
    }
    if (Object.keys(values).length <= 1) {
      setNotice({
        tone: "error",
        text: "Enter the reviewed inputs before submitting.",
      });
      return;
    }
    setSubmitting(true);
    setNotice(null);
    void (async () => {
      try {
        const result = await invokeClose("costing.review.finalize", values);
        await onReviewed(
          result,
          values,
          approvalSummaryFromResult(result),
        );
        setPreview(null);
        setPreviewKey("");
        setNotice({
          tone: "saved",
          text: "Reviewed inputs saved to Accounting.",
        });
      } catch {
        setNotice({
          tone: "error",
          text: "The reviewed inputs are incomplete or could not be accepted.",
        });
      } finally {
        setSubmitting(false);
      }
    })();
  }

  function runPreview() {
    if (!evidenceReady) {
      setNotice({
        tone: "error",
        text: "Import the required METRC reports before previewing this close.",
      });
      return;
    }
    if (
      submitting ||
      previewing ||
      !hostContext ||
      !scope?.organization_id ||
      state.status !== "loaded"
    )
      return;
    const values = readValues();
    const key = previewFingerprint({
      values,
      evidenceKey,
      periodStart: context.period.start,
      periodEnd: context.period.end,
    });
    setPreviewing(true);
    setNotice(null);
    void (async () => {
      try {
        const result = await invokeClose("costing.review.preview", values);
        const next = parseCultivationPreview(result);
        setPreview(next);
        setPreviewKey(key);
      } catch (cause) {
        setNotice({
          tone: "error",
          text:
            cause instanceof Error
              ? cause.message
              : "The close preview could not be built.",
        });
      } finally {
        setPreviewing(false);
      }
    })();
  }

  function accountOptions(current: string): ChartAccount[] {
    const seen = new Set<string>();
    const options: ChartAccount[] = [];
    for (const account of accounts) {
      if (!account.active && account.code !== current) continue;
      if (seen.has(account.code)) continue;
      seen.add(account.code);
      options.push(account);
    }
    return options;
  }

  function accountName(code: string): string {
    return accounts.find((account) => account.code === code)?.name ?? "";
  }

  if (state.status === "loading") {
    return (
      <p role="status" className="text-sm text-[var(--muted-foreground)]">
        Loading close review…
      </p>
    );
  }
  if (state.status === "error") {
    return (
      <p role="alert" className="text-sm text-[var(--danger)]">
        {state.message}
      </p>
    );
  }

  const evidenceReady = state.surface.metadata?.evidence_ready !== false && state.surface.metadata?.empty !== true;
  const carriedPlantMonths = typeof state.surface.metadata?.carried_plant_months === "string"
    ? state.surface.metadata.carried_plant_months
    : "";
  const showInputs = reviewFieldCount(state.surface.body) > 0 || accountFields.length > 0;
  return (
    <div className="grid gap-3">
      {carried ? (
        <p className="text-sm text-[var(--muted-foreground)]">
          Settings carried from {carried.month}. Phase weeks, rates, the
          reserve, accounts, allocations, and the excise rate can be changed for
          this close.
        </p>
      ) : null}
      {!evidenceReady ? (
        <p
          role="status"
          className="rounded-xl border border-[var(--border)] p-3 text-sm text-[var(--muted-foreground)]"
        >
          Manual cost inputs are available now. Preview and save unlock when the required METRC reports are ready.
        </p>
      ) : null}
      {carriedPlantMonths ? (
        <p
          role="status"
          className="rounded-xl border border-[var(--border)] p-3 text-sm text-[var(--muted-foreground)]"
        >
          No plant-inventory report for {carriedPlantMonths}; counts carry the prior
          ending balances.
        </p>
      ) : null}
      {disabled ? (
        <p
          role="status"
          className="rounded-xl border border-[var(--border)] p-3 text-sm text-[var(--muted-foreground)]"
        >
          This review is {review?.state.replaceAll("_", " ")}. Inputs are
          read-only until the period is reopened.
        </p>
      ) : null}
      <div
        id="review-inputs"
        ref={panelRef}
        className={
          disabled ? "pointer-events-none grid gap-4 opacity-60" : "grid gap-4"
        }
        aria-disabled={disabled || undefined}
      >
        {showInputs ? (
          <fieldset
            className="grid gap-2 rounded-xl border border-[var(--border)] p-4"
            disabled={disabled}
          >
            <legend className="px-1 text-sm font-semibold">
              Journal families
            </legend>
            {(["wip", "directexall", "cogs"] as const).map((family) => (
              <label key={family} className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={families[family]}
                  onChange={(event) =>
                    setFamilies((current) => ({
                      ...current,
                      [family]: event.target.checked,
                    }))
                  }
                  disabled={disabled}
                />
                {family === "wip"
                  ? "WIP"
                  : family === "directexall"
                    ? "Direct expense allocation"
                    : "COGS"}
              </label>
            ))}
          </fieldset>
        ) : null}
        {showInputs && accountFields.length > 0 ? (
          <fieldset
            className="grid gap-3 rounded-xl border border-[var(--border)] p-4"
            disabled={disabled}
          >
            <legend className="px-1 text-sm font-semibold">
              Account mapping
            </legend>
            <p className="text-xs text-[var(--muted-foreground)]">
              Choose each posting account from the active chart of accounts.
            </p>
            <div className="grid gap-3 sm:grid-cols-2">
              {accountFields.map((field) => {
                const current =
                  accountValues[field.name] ??
                  savedValues?.[field.name] ??
                  initialValues?.[field.name] ??
                  "";
                const options = accountOptions(current);
                return (
                  <label
                    key={field.name}
                    className="grid gap-1 text-xs font-medium"
                  >
                    {field.label}
                    <select
                      name={field.name}
                      className="min-h-10 rounded-md border border-[var(--border)] bg-[var(--surface)] px-3 text-sm"
                      value={current}
                      onChange={(event) => {
                        setAccountValues((values) => ({
                          ...values,
                          [field.name]: event.target.value,
                        }));
                      }}
                      disabled={disabled}
                    >
                      <option value="">Choose an account</option>
                      {options.map((account) => (
                        <option key={account.code} value={account.code}>
                          {account.code} — {account.name}
                        </option>
                      ))}
                      {current &&
                      !options.some((account) => account.code === current) ? (
                        <option value={current}>
                          {current} — saved account
                        </option>
                      ) : null}
                    </select>
                  </label>
                );
              })}
            </div>
          </fieldset>
        ) : null}
        {showInputs ? (
          <fieldset
            className="grid gap-3 rounded-xl border border-[var(--border)] p-4"
            disabled={disabled}
          >
            <legend className="px-1 text-sm font-semibold">
              Direct expense allocations
            </legend>
            <p className="text-xs text-[var(--muted-foreground)]">
              Select the account; its name is filled from the chart of accounts.
              Enter a percent or a dollar amount.
            </p>
            {allocations.map((row, index) => (
              <div
                key={index}
                className="grid gap-2 rounded-lg border border-[var(--border)] p-3 sm:grid-cols-[minmax(0,1.6fr)_minmax(0,0.8fr)_minmax(0,0.8fr)_auto_auto] sm:items-end"
              >
                <label className="grid gap-1 text-xs font-medium">
                  Account
                  <select
                    className="min-h-10 rounded-md border border-[var(--border)] bg-[var(--surface)] px-3 text-sm"
                    name={`allocation_${index}_account`}
                    value={row.account}
                    onChange={(event) => {
                      allocationsTouchedRef.current = true;
                      const account = event.target.value;
                      setAllocations((current) =>
                        current.map((item, itemIndex) =>
                          itemIndex === index
                            ? { ...item, account, name: accountName(account) }
                            : item,
                        ),
                      );
                    }}
                    disabled={disabled}
                  >
                    <option value="">Choose an account</option>
                    {accountOptions(row.account).map((account) => (
                      <option key={account.code} value={account.code}>
                        {account.code} — {account.name}
                      </option>
                    ))}
                    {row.account &&
                    !accountOptions(row.account).some(
                      (account) => account.code === row.account,
                    ) ? (
                      <option value={row.account}>
                        {row.account} — {row.name || "saved account"}
                      </option>
                    ) : null}
                  </select>
                </label>
                <label className="grid gap-1 text-xs font-medium">
                  Amount
                  <input
                    className="input"
                    type="number"
                    step="0.01"
                    min="0"
                    name={`allocation_${index}_amount`}
                    value={row.amount}
                    onChange={(event) => {
                      allocationsTouchedRef.current = true;
                      setAllocations((current) =>
                        current.map((item, itemIndex) =>
                          itemIndex === index
                            ? { ...item, amount: event.target.value }
                            : item,
                        ),
                      );
                    }}
                    disabled={disabled}
                  />
                </label>
                <label className="grid gap-1 text-xs font-medium">
                  Percent
                  <input
                    className="input"
                    type="number"
                    step="0.01"
                    min="0"
                    max="100"
                    name={`allocation_${index}_percent`}
                    value={row.percent}
                    onChange={(event) => {
                      allocationsTouchedRef.current = true;
                      setAllocations((current) =>
                        current.map((item, itemIndex) =>
                          itemIndex === index
                            ? { ...item, percent: event.target.value }
                            : item,
                        ),
                      );
                    }}
                    disabled={disabled}
                  />
                </label>
                <label className="flex items-center gap-2 self-end pb-2 text-xs font-medium">
                  <input
                    type="checkbox"
                    checked={row.labor === "yes"}
                    onChange={(event) => {
                      allocationsTouchedRef.current = true;
                      setAllocations((current) =>
                        current.map((item, itemIndex) =>
                          itemIndex === index
                            ? {
                                ...item,
                                labor: event.target.checked ? "yes" : "",
                              }
                            : item,
                        ),
                      );
                    }}
                    disabled={disabled}
                  />
                  Direct labor
                </label>
                {allocations.length > 1 ? (
                  <button
                    type="button"
                    className="self-end pb-2 text-xs text-[var(--danger)]"
                    onClick={() => {
                      allocationsTouchedRef.current = true;
                      setAllocations((current) =>
                        current.filter((_, itemIndex) => itemIndex !== index),
                      );
                    }}
                    disabled={disabled}
                  >
                    Remove
                  </button>
                ) : null}
              </div>
            ))}
            <button
              type="button"
              className="justify-self-start text-xs text-[var(--action)]"
              onClick={() => {
                allocationsTouchedRef.current = true;
                setAllocations((current) => [
                  ...current,
                  {
                    pool: "",
                    account: "",
                    name: "",
                    amount: "",
                    percent: "",
                    labor: "",
                  },
                ]);
              }}
              disabled={disabled || allocations.length >= 100}
            >
              Add allocation
            </button>
          </fieldset>
        ) : null}
        {viewSurface ? (
          <ReviewInputGroups
            surface={
              scope
                ? scopeExtensionViewSurface(viewSurface, scope)
                : viewSurface
            }
            onCommand={(invocation) => {
              if (invocation.command === "costing.review.finalize") {
                submitFinal();
                return;
              }
              if (invocation.command === "costing.review.save") {
                setNotice({
                  tone: "saved",
                  text: "Values kept. Submit the review when every section is complete.",
                });
              }
            }}
          />
        ) : null}
      </div>
      {showInputs ? (
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant="secondary"
            onClick={runPreview}
            disabled={
              disabled || !evidenceReady || submitting || previewing || state.status !== "loaded"
            }
          >
            {previewing ? "Previewing…" : "Preview journals"}
          </Button>
          <Button
            type="button"
            onClick={submitFinal}
            disabled={
              disabled || !evidenceReady || submitting || previewing || state.status !== "loaded"
            }
          >
            {submitting ? "Saving…" : (submitLabel ?? "Submit reviewed inputs")}
          </Button>
          {onOpenImports ? (
            <Button type="button" variant="ghost" onClick={onOpenImports}>
              Open imports
            </Button>
          ) : null}
        </div>
      ) : null}
      {showInputs && changed.length > 0 ? (
        <section
          className="surface grid gap-2 rounded-xl border border-[var(--border)] p-4"
          aria-label="Changed since last save"
        >
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-sm font-semibold">Changed since last save</h3>
            <Badge tone="warning">{changed.length}</Badge>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr>
                  <th className="border-b border-[var(--border)] px-2 py-1 text-left">
                    Field
                  </th>
                  <th className="border-b border-[var(--border)] px-2 py-1 text-left">
                    Saved
                  </th>
                  <th className="border-b border-[var(--border)] px-2 py-1 text-left">
                    Current draft
                  </th>
                  <th className="border-b border-[var(--border)] px-2 py-1 text-left">
                    Source
                  </th>
                </tr>
              </thead>
              <tbody>
                {changed.map((row) => (
                  <tr key={row.key}>
                    <td className="px-2 py-1">{row.label}</td>
                    <td className="px-2 py-1 text-[var(--muted-foreground)]">
                      {row.saved || "—"}
                    </td>
                    <td className="px-2 py-1">{row.current || "—"}</td>
                    <td className="px-2 py-1">
                      <Badge
                        tone={
                          row.source === "Overridden" ? "warning" : "neutral"
                        }
                      >
                        {row.source}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}
      {preview ? (
        <section
          className="surface grid gap-3 rounded-xl border border-[var(--border)] p-4"
          aria-label="Close preview"
        >
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-sm font-semibold">Preview readiness</h3>
            <Badge
              tone={
                hostBlockers.length > 0 || preview.status === "blocked"
                  ? "danger"
                  : preview.status === "reviewable"
                    ? "success"
                    : "warning"
              }
            >
              {hostBlockers.length > 0
                ? "blocked"
                : preview.status.replaceAll("_", " ")}
            </Badge>
            {previewStale ? (
              <Badge tone="warning">Stale — refresh preview</Badge>
            ) : null}
            <span className="text-xs text-[var(--muted-foreground)]">
              Preview only. Nothing is saved, posted, or locked.
            </span>
          </div>
          {hostBlockers.length + preview.blockers.length > 0 ? (
            <div className="grid gap-1 rounded-lg border border-[var(--danger)] p-3 text-sm">
              <p className="font-medium text-[var(--danger)]">Blockers</p>
              <ul className="grid gap-1 text-[var(--muted-foreground)]">
                {[...hostBlockers, ...preview.blockers].map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
              <div className="flex flex-wrap gap-3 text-xs">
                {onOpenImports ? (
                  <button
                    type="button"
                    className="justify-self-start text-[var(--action)] underline-offset-4 hover:underline"
                    onClick={onOpenImports}
                  >
                    Open imports to fix evidence
                  </button>
                ) : null}
                <a
                  className="text-[var(--action)] underline-offset-4 hover:underline"
                  href="#review-inputs"
                >
                  Review inputs to fix a value
                </a>
                <a
                  className="text-[var(--action)] underline-offset-4 hover:underline"
                  href="/settings?tab=books&section=period-locks"
                >
                  Open period locks
                </a>
              </div>
            </div>
          ) : null}
          {preview.warnings.length > 0 ? (
            <div className="grid gap-1 rounded-lg border border-[var(--warning)] p-3 text-sm">
              <p className="font-medium text-[var(--warning)]">Warnings</p>
              <ul className="grid gap-1 text-[var(--muted-foreground)]">
                {preview.warnings.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </div>
          ) : null}
          {preview.requirements.approval_required ? (
            <div className="grid gap-1 rounded-lg border border-[var(--warning)] p-3 text-sm">
              <p className="font-medium text-[var(--warning)]">
                Approval required before posting
              </p>
              <ul className="grid gap-1 text-[var(--muted-foreground)]">
                {preview.requirements.reasons.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </div>
          ) : null}
          {preview.journals.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr>
                    <th className="border-b border-[var(--border)] px-2 py-1 text-left">
                      Journal
                    </th>
                    <th className="border-b border-[var(--border)] px-2 py-1 text-left">
                      Debit
                    </th>
                    <th className="border-b border-[var(--border)] px-2 py-1 text-left">
                      Credit
                    </th>
                    <th className="border-b border-[var(--border)] px-2 py-1 text-left">
                      Residual
                    </th>
                    <th className="border-b border-[var(--border)] px-2 py-1 text-left">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {preview.journals.map((journal) => (
                    <tr key={journal.family}>
                      <td className="px-2 py-1">
                        {journal.name ?? journal.family}
                      </td>
                      <td className="px-2 py-1">
                        {formatCents(journal.debit_total_cents)}
                      </td>
                      <td className="px-2 py-1">
                        {formatCents(journal.credit_total_cents)}
                      </td>
                      <td className="px-2 py-1">
                        {formatCents(journal.residual_cents ?? 0)}
                      </td>
                      <td className="px-2 py-1">
                        <Badge tone={journal.balanced ? "success" : "danger"}>
                          {journal.error ??
                            (journal.balanced ? "balanced" : "unbalanced")}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
          {preview.sections.length > 0 && activeSchedule ? (
            <div className="grid gap-2">
              <h3 className="text-sm font-semibold">Workbook schedules</h3>
              <Tabs
                tabs={preview.sections.map((section) => ({
                  value: section.id,
                  label: section.title,
                }))}
                value={activeSchedule.id}
                onValueChange={setScheduleTab}
                label="Close workbook schedules"
              >
                <div className="grid gap-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge
                      tone={
                        activeSchedule.status === "blocked"
                          ? "danger"
                          : activeSchedule.status === "needs_review"
                            ? "warning"
                            : "success"
                      }
                    >
                      {activeSchedule.status.replaceAll("_", " ")}
                    </Badge>
                    <span className="text-xs text-[var(--muted-foreground)]">
                      Preview only. The locked close renders the final workbook.
                    </span>
                  </div>
                  {activeSchedule.warnings.length > 0 ? (
                    <ul className="grid gap-1 text-sm text-[var(--warning)]">
                      {activeSchedule.warnings.map((warning) => (
                        <li key={warning}>{warning}</li>
                      ))}
                    </ul>
                  ) : null}
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr>
                          {activeSchedule.columns.map((column) => (
                            <th
                              key={column}
                              className="border-b border-[var(--border)] px-2 py-1 text-left"
                            >
                              {column}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {activeSchedule.rows.map((row, rowIndex) => (
                          <tr key={`${activeSchedule.id}-${rowIndex}`}>
                            {row.map((value, valueIndex) => (
                              <td
                                key={`${activeSchedule.id}-${rowIndex}-${valueIndex}`}
                                className="px-2 py-1"
                              >
                                {value}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </Tabs>
            </div>
          ) : null}
        </section>
      ) : null}
      {notice ? (
        <p
          role={notice.tone === "error" ? "alert" : "status"}
          className={`text-sm ${notice.tone === "error" ? "text-[var(--danger)]" : "text-[var(--muted-foreground)]"}`}
        >
          {notice.text}
        </p>
      ) : null}
    </div>
  );
}
